import {Button} from "@/components/ui/button.tsx";
import {GroupStates, StateCard} from "@/components/ui/state-card.tsx";
import {useSumCallsQuery} from "@/store/systems.ts";
import {useEffect, useMemo, useState} from "react";
import {useUser} from "@/hooks/useUser.ts";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import {CalendarIcon} from "@heroicons/react/24/outline";
import {format} from "date-fns";
import {Calendar} from "@/components/ui/calendar.tsx";
import {DateRange} from "react-day-picker";
import moment from "moment";
import {he} from "date-fns/locale";

export function SumCallsSystem() {

    const [isCustomDate, setIsCustomDate] = useState(false);
    const [open, setOpen] = useState(false);

    const {user} = useUser();

    const [date, setDate] = useState<DateRange>({
        from: new Date,
        to: new Date,
    });

    useEffect(() => {
        if(!open && currentPreset) {
            setIsCustomDate(false);
        }
    }, [open]);


    const {data, isLoading, isUninitialized, error} = useSumCallsQuery({
        start: format(date.from ?? new Date, "yyyy-MM-dd"),
        end: format(date.to ?? new Date, "yyyy-MM-dd")
    }, {skip: !user});


    const presets = [
        {id: 'today', label: "היום", from: new Date, to: new Date},
        {id: 'currentWeek', label: "השבוע", from: moment().startOf('week').toDate(), to: moment().endOf('week').toDate()},
        {id: 'currentMonth', label: "החודש", from: moment().startOf('month').toDate(), to: moment().endOf('month').toDate()},
        {id: 'currentYear', label: "השנה", from: moment().startOf('year').toDate(), to: moment().endOf('year').toDate()},
        {id: 'last7Days', label: '7 ימים אחרונים', from: moment().subtract(7, 'days').toDate(), to: new Date},
        {id: 'last30Days', label: '30 יום אחרונים', from: moment().subtract(30, 'days').toDate(), to: new Date},
        {id: 'last90Days', label: '90 יום אחרונים', from: moment().subtract(90, 'days').toDate(), to: new Date},
    ];

    const currentPreset = useMemo(() => {
        return presets.find(p => moment(p.from).isSame(date.from, 'day') && moment(p.to).isSame(date.to, 'day'));
    }, [date]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between">
                <div>
                    <h2 className="text font-medium">מערכת שיחות נכנסות</h2>
                </div>

                <div>
                    <div className={cn("grid gap-2")}>
                        <Popover
                            open={open}
                            onOpenChange={setOpen}
                        >
                            <PopoverTrigger asChild>
                                <Button
                                    id="date"
                                    variant={"outline"}
                                    className={cn(
                                        "justify-start font-normal",
                                        !date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon />
                                    {isCustomDate && (
                                        <>
                                            {format(date.from ?? new Date, "dd בLLL, y", {locale: he})} עד{" "}
                                            {format(date.to ?? new Date, "dd בLLL, y", {locale: he})}
                                        </>
                                    )}
                                    {!isCustomDate && currentPreset?.label}

                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">

                                <div className="flex gap-2">
                                    <div className="w-40 p-2 border-e border-gray-200 last:border-e-0">
                                        {presets.map((preset) => (
                                            <Button
                                                key={preset.label}
                                                variant="ghost"
                                                onClick={() => {
                                                    setIsCustomDate(false);
                                                    setDate({
                                                        from: preset.from,
                                                        to: preset.to,
                                                    });
                                                }}
                                                size="sm"
                                                className={cn(
                                                    "w-full justify-start",
                                                    currentPreset?.id === preset.id && ! isCustomDate && "bg-neutral-200 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-800 text-neutral-900 dark:text-neutral-50"
                                                )}
                                            >
                                                {preset.label}
                                            </Button>
                                        ))}

                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setIsCustomDate(true)}
                                            className={cn("w-full justify-start",
                                                isCustomDate && "bg-neutral-200 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-800 text-neutral-900 dark:text-neutral-50"
                                            )}
                                        >
                                            בחר תאריכים
                                        </Button>
                                    </div>

                                    {isCustomDate && (
                                        <Calendar
                                            initialFocus
                                            mode="range"
                                            defaultMonth={date?.from}
                                            selected={date}
                                            /* @ts-ignore */
                                            onSelect={setDate}
                                            numberOfMonths={2}
                                            weekStartsOn={0}
                                            disabled={date => date > new Date}
                                        />
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            </div>
            <div>
                <GroupStates>
                    <StateCard
                        title="מתקשרים"
                        value={Intl.NumberFormat().format(data?.sum_users ?? 0)}
                        link="learning/calllog/"
                        linkText="לפירוט המלא"
                    />
                    <StateCard
                        title="שיחות נכנסות"
                        value={Intl.NumberFormat().format(data?.sum_calls ?? 0)}
                        link="learning/calllog/"
                        linkText="לפירוט המלא"
                    />
                    <StateCard
                        title="דקות שיחה"
                        value={Intl.NumberFormat().format(data?.sum_minutes ?? 0)}
                        link="learning/calllog/"
                        linkText="לפירוט המלא"
                    />
                </GroupStates>
            </div>
        </div>
    )
}